<template>
  <div
    :id="id"
    :class="`${
      isShowLabel
        ? 'tw-pt-[25px] tw-mt-[15px] tw-border-0 tw-border-t-[1px] tw-border-solid tw-border-[#EFF0F1] '
        : 'xl:tw-py-[15px] xl:tw-px-[58px] tw-py-[25px] tw-pl-[58px] tw-pr-[25px] tw-border-0 tw-border-t-[1px] tw-border-solid tw-border-[#EFF0F1] tw-rounded-b-[6px] tw-bg-[#FCFCFD]'
    } `"
  >
    <div
      v-if="isShowLabel"
      class="tw-text-[13px] tw-font-[500] tw-text-[#211F58] tw-leading-[130%] tw-pb-[15px]"
    >
      {{
        getComponentUiText(
          "app-component-assessments-vendor-review-title-evaluate-1",
        )
      }}
    </div>
    <div class="tw-flex tw-gap-[25px] tw-items-center tw-flex-wrap">
      <div
        class="dropdown-container tw-flex-1 dropdown tw-max-w-[300px]"
        :data-cy="`evaluate-dropdown-btn-${idQuestion}`"
      >
        <CustomBDropDown
          :id="idDropdown"
          :data="answerOption"
          :class-name="`gray-dropdown-container tw-h-[42px] ${
            backgroundInput === 'white'
              ? 'white-background'
              : 'tw-bg-[rgba(239,240,241,0.5)'
          }`"
          :class-text-placeholder="'tw-leading-none tw-p-0  tw-border-none'"
          :placeholder="
            getComponentUiText(
              'app-component-assessments-vendor-review-placeholder-evaluate-1',
            )
          "
        >
          <CustomBDropDownItem
            v-for="item in answerOptionsDropDown"
            :key="item.id"
            :data="item"
            :class-name="'normal-dropdown-container'"
            :data-cy-id="`evaluate-dropdown-item-${item.id}`"
            @click="handleUpdateEvaluate(item)"
          />
        </CustomBDropDown>
      </div>

      <PercentInput
        :value="valueNumberOver ?? 0"
        hint-text="%"
        :label="
          getComponentUiText('app-component-assessments-override-score-title-1')
        "
        :class-input="`!tw-h-[42px] tw-text-[11px] !tw-py-[15px] !tw-bg-[${backgroundInput}]`"
        class-text="tw-text-[11px] tw-text-[#231F20] tw-leading-[130%] tw-font-medium "
        :data-cy-id="`override-score-input-${idQuestion}`"
        :disabled="disablePercentInput"
        :id-percent-input="idPercentInput"
        @on:blur="focusOutUpdateOverrideScore"
        @update:model-value="handleUpdateOverrideScore"
      />

      <SimpleInput
        :id="idNoteInput"
        type="input"
        class="tw-min-w-[250px] tw-flex-1 !tw-h-[42px]"
        :data-cy="`question-note-input-${idQuestion}`"
        :class-style="`!tw-h-[42px] !tw-bg-[${backgroundInput}] !tw-font-normal`"
        :placeholder="
          getComponentUiText(
            'app-component-assessments-question-type-2-notes-placeholder-1',
          )
        "
        :disabled="isChecklistCompleted"
        :value="noteValueRef"
        :is-show-icon="true"
        @on:blur="handleBlurNotes"
        @update:value="handleUpdateNote"
      />
    </div>
    <div
      v-if="answerOption[0]?.id === infomationRequiredId"
      class="tw-relative"
    >
      <SimpleInput
        id="informationRequired"
        :data-cy="`information-required-input-${idQuestion}`"
        type="input"
        :class-style="`!tw-h-[42px] !tw-font-normal tw-mt-[15px] ${
          $v.informationValue?.$error
            ? '!tw-border !tw-border-[#f7553c] !tw-bg-[#fee5e2]'
            : `!tw-bg-[${backgroundInput}]`
        }`"
        :has-error="!!$v.informationValue?.$errors"
        :placeholder="
          getComponentUiText(
            'app-component-assessments-vendor-review-information-required-placeholder-1',
          )
        "
        :disabled="isChecklistCompleted"
        :value="form.informationValue"
        :is-show-icon="true"
        @on:blur="handleBlurInformation"
        @update:value="handleUpdateInformation"
      />
      <span
        v-if="!form.informationValue"
        class="asterisk"
        style="position: absolute; top: 6px; color: red; left: 238px"
        >*</span
      >
      <b-form-invalid-feedback
        v-if="$v.informationValue?.$errors"
        :class="{ 'is-invalid': $v.informationValue?.$error }"
      >
        <VRuntimeTemplate
          :template="$v.informationValue?.$errors?.[0]?.$message"
        />
      </b-form-invalid-feedback>
    </div>
  </div>
</template>

<script lang="ts" setup>
import {
  computed,
  defineProps,
  onMounted,
  onUnmounted,
  Ref,
  ref,
  watch,
} from "vue";
import { getComponentUiText } from "@/utils/utilities";
import CustomBDropDown from "@/components/common/customBDropDown.vue";
import CustomBDropDownItem from "@/components/common/customBDropDownItem.vue";
import PercentInput from "@/components/common/percentInput.vue";
import SimpleInput from "@/components/common/simpleInput.vue";
import store from "@/store";
import { infomationRequiredId } from "@/utils/constants";
import { debounce } from "lodash";
import VRuntimeTemplate from "vue3-runtime-template";
import { useRoute } from "vue-router";
import useVuelidate from "@vuelidate/core";
import { helpers, required } from "@vuelidate/validators";
import eventEmitter, { EmitterEvents } from "@/utils/eventEmitter";

const route = useRoute();
const assessmentId = Number(route.params.assessmentId);

const props = defineProps({
  checklistItemDataRaw: { type: Object, required: true },
  isChecklistCompleted: { type: Boolean, required: false, default: false },
  isShowLabel: { type: Boolean, required: false, default: true },
  backgroundInput: {
    type: String,
    required: false,
    default: "rgba(239,240,241,0.5)",
  },
  id: { type: String, required: false, default: "" },
  idQuestion: { type: String, required: false, default: "" },
  idDropdown: { type: String, required: false, default: "" },
  idPercentInput: { type: String, required: false, default: "" },
  idNoteInput: { type: String, required: false, default: "" },
  labelEvaluate: { type: String, required: false, default: "" },
});

const noteValue = computed(
  () => props.checklistItemDataRaw.checklistItemNotesOrgInternal,
);

const answerOptionsDropDown = computed(() => {
  const data = store.state.Assessment.assessmentQuestionTypes?.find(
    (item) =>
      item.id === props.checklistItemDataRaw?.checklistItemComplianceTypeOrgId,
  );

  if (!data) return [];

  return data.complianceTypeValues.map((item) => ({
    ...item,
    id: item.id,
    value: item.id,
    name: item.label,
  }));
});

const disablePercentInput = ref(true);
const answerOption: Ref<any> = ref([null]);
const valueNumberOver = ref(
  props.checklistItemDataRaw.checklistItemComplianceOverride,
);
const noteValueRef = ref(noteValue.value);

const form = ref({
  informationValue: props.checklistItemDataRaw.checklistItemNotesOrgExternal,
});

const rules = {
  informationValue: {
    required: helpers.withMessage(
      () => getComponentUiText("app-common-form-value-required-error-1"),
      required,
    ),
  },
};

const $v = useVuelidate(rules, form);

const emits = defineEmits(["update:evaluate-answer"]);

const handleUpdateEvaluate = async (answer: any) => {
  answerOption.value = [
    answerOptionsDropDown.value.find((item) => item.value == answer.value),
  ];

  const validate =
    answerOption.value[0].id === infomationRequiredId
      ? form.value.informationValue
      : true;

  if (!validate) return;
  disablePercentInput.value = false;

  const res = await store.dispatch("Vendor/updateRequestedItems", {
    id: assessmentId,
    values: [
      {
        notesOrgExternal:
          answerOption.value[0].id === infomationRequiredId
            ? form.value.informationValue
            : null,
        complianceTypeValueOrgId: answer.id,
        checklistRequestedItemId: props.checklistItemDataRaw.checklistItemId,
      },
    ],
  });

  if (!res) {
    answerOption.value = answerOptionsDropDown.value.filter(
      (item) =>
        item.id ===
        props.checklistItemDataRaw.checklistItemComplianceTypeValueOrgId,
    );
  }

  answer.id &&
    emits("update:evaluate-answer", {
      complianceTypeValueOrgId: res
        ? answer.id
        : answerOption.value && answerOption.value[0]
          ? answerOption.value[0].id
          : [],
    });
};

const focusOutUpdateOverrideScore = debounce(async () => {
  if (
    valueNumberOver.value ===
    props.checklistItemDataRaw.checklistItemComplianceOverride
  )
    return;
  const res = await store.dispatch("Vendor/updateRequestedItems", {
    id: assessmentId,
    values: [
      {
        complianceOverride: valueNumberOver.value,
        checklistRequestedItemId: props.checklistItemDataRaw.checklistItemId,
      },
    ],
  });
  if (!res) {
    valueNumberOver.value =
      props.checklistItemDataRaw.checklistItemComplianceOverride;
  }
}, 450);

const handleUpdateOverrideScore = debounce((value: number) => {
  let valueNumber = Number(value);
  if (isNaN(valueNumber)) return;
  if (valueNumber < 0) valueNumber = 0;
  if (valueNumber > 100) valueNumber = 100;

  valueNumberOver.value = valueNumber / 100;

  // emits("update:evaluate-answer", { complianceOverride: valueNumber / 100 });
}, 450);

const handleUpdateNote = debounce((value: string) => {
  const objectUpdated = { notesOrgInternal: value };
  noteValueRef.value = objectUpdated["notesOrgInternal"];
  emits("update:evaluate-answer", objectUpdated);
}, 450);

const handleBlurNotes = debounce(async () => {
  if (noteValueRef.value === noteValue.value) return;

  const values = {
    notesOrgInternal: noteValueRef.value,
    checklistRequestedItemId: props.checklistItemDataRaw.checklistItemId,
  };
  const res = await store.dispatch("Vendor/updateRequestedItems", {
    id: assessmentId,
    values: [values],
  });

  if (!res) {
    noteValueRef.value = noteValue.value;
  }
}, 500);

const handleUpdateInformation = debounce((value: string) => {
  const objectUpdated = {};
  form.value.informationValue = value;

  objectUpdated["notesOrgExternal"] = value;
  emits("update:evaluate-answer", objectUpdated);
}, 40);

const handleBlurInformation = debounce(async () => {
  const isValid = await $v.value.$validate();

  if (!isValid) return;
  if (
    form.value.informationValue ===
    props.checklistItemDataRaw.checklistItemNotesOrgExternal
  )
    return;

  let values;
  if (
    props.checklistItemDataRaw.checklistItemComplianceTypeOrgId !==
    infomationRequiredId
  ) {
    values = {
      notesOrgExternal: form.value.informationValue,
      complianceTypeValueOrgId: infomationRequiredId,
      checklistRequestedItemId: props.checklistItemDataRaw.checklistItemId,
    };
  } else {
    values = {
      notesOrgExternal: form.value.informationValue,
      checklistRequestedItemId: props.checklistItemDataRaw.checklistItemId,
    };
  }

  const res = await store.dispatch("Vendor/updateRequestedItems", {
    id: assessmentId,
    values: [values],
  });

  if (!res) {
    noteValueRef.value = noteValue.value;
  }
}, 50);

const handleBindingEvaluate = () => {
  if (
    answerOptionsDropDown.value.length === 0 ||
    props.checklistItemDataRaw.checklistItemComplianceTypeValueOrgId === null
  )
    return;

  const result = answerOptionsDropDown.value.find(
    (item) =>
      item.id ===
      props.checklistItemDataRaw.checklistItemComplianceTypeValueOrgId,
  );

  // Enable Override score if review have value.
  answerOption.value = [result];
  answerOption.value && (disablePercentInput.value = false);
};

onMounted(() => {
  handleBindingEvaluate();
  eventEmitter.on(EmitterEvents.SAVE_AND_CLOSE_REVIEW_VENDOR, async () => {
    const isValid =
      answerOption.value[0]?.id === infomationRequiredId
        ? await $v.value.$validate()
        : true;
    if (!props.checklistItemDataRaw?.checklistItemId) {
      eventEmitter.emit(EmitterEvents.VENDOR_ITEM_INVALID);
      return;
    }
    eventEmitter.emit(EmitterEvents.VENDOR_ITEM_VALID_SUCCESS, {
      checklistItemId: props.checklistItemDataRaw?.checklistItemId,
      isValid,
    });
  });
});

onUnmounted(() => {
  const route = useRoute();
  if (!route.path.includes("/assess/vendor/review")) {
    eventEmitter.off(EmitterEvents.SAVE_AND_CLOSE_REVIEW_VENDOR);
  }
});
</script>

<style scoped>
.gray-dropdown-container .btn-secondary {
  padding: 12px !important;
}
.is-invalid-information {
  border: 1px solid #f7553c !important;
  background: #fee5e2 !important;

  &::after {
    content: "";
  }
}
</style>
